import { Fragment } from 'react';

/**
 * A modal component that presents a binary choice (confirm/reject).
 *
 * @component
 * @param {Object} props - The props object.
 * @param {string} props.title - The title of the modal.
 * @param {string} props.confirmButtonText - The text for the confirm button.
 * @param {string} [props.rejectButtonText] - The text for the reject button.
 * @param {Function|false} [props.onConfirm=false] - Callback function when the confirm button is clicked.
 * @param {Function|false} [props.onReject=false] - Callback function when the reject button is clicked.
 * @param {string} [props.message] - The message displayed in the modal.
 * @param {boolean} [props.verticalButtons=false] - Whether the buttons are displayed vertically.
 * @param {Object} [props.style] - Custom styles applied to the modal container.
 * @param {string} [props.dataAt] - Data attribute for testing.
 * @param {React.ReactNode} [props.children] - Additional content inside the modal.
 * @param {DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>} [props.rest] - Additional props passed to the root div.
 *
 * @returns {JSX.Element} The rendered modal component.
 */
const ModalBinary = ({
	title,
	confirmButtonText,
	rejectButtonText,
	onConfirm = false,
	onReject = false,
	message,
	verticalButtons = false,
	style,
	dataAt,
	children,
	...rest
}) => (
	<div
		{...rest}
		data-at={dataAt}
		className="js-binary-question binary-question u-fxb-center-v-h notify notify--is-open"
	>
		<div className="binary-question__inner u-b-radius" style={style}>
			<div className="binary-question__head u-p2 u-text-c">
				<h2 className="js-title binary-question__main-title">{title}</h2>
				<p className="js-text binary-question__p">{message}</p>
				{children && <div className="js-text binary-question__content">{children}</div>}
			</div>
			{verticalButtons ? (
				<Fragment>
					{onConfirm && (
						<div className="binary-question__button-container">
							<button
								data-at="binary-yes"
								className="js-button-wrap-yes binary-question__yes btn btn--secondary"
								onClick={onConfirm}
							>
								{confirmButtonText}
							</button>
						</div>
					)}
					{onReject && (
						<div className="binary-question__button-container u-b-radius-bottom">
							<button
								data-at="binary-no"
								className="js-button-wrap-no binary-question__no btn btn--secondary"
								onClick={onReject}
							>
								{rejectButtonText}
							</button>
						</div>
					)}
				</Fragment>
			) : (
				<div className="binary-question__button-container u-b-radius-bottom">
					{onReject && (
						<button
							data-at="bin-no"
							className="js-button-wrap-no binary-question__no btn btn--secondary"
							onClick={onReject}
						>
							{rejectButtonText}
						</button>
					)}
					{onConfirm && (
						<button
							data-at="bin-yes"
							className="js-button-wrap-yes binary-question__yes btn btn--secondary"
							onClick={onConfirm}
						>
							{confirmButtonText}
						</button>
					)}
				</div>
			)}
		</div>
	</div>
);

export default ModalBinary;
